import { Divider, Icon, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import {
  WorksheetCostCategory,
  WorksheetHeaderUnit,
  WorksheetMaterialCalcMethod,
} from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { HelperResolution } from "utils/worksheet/worksheetHelpers";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  WORKSHEET_CATEGORY_LABELS,
  WorksheetLibrarySelection,
  applyLibrarySelectionToRow,
  getMovedCostRow,
  getRowCost,
  getRowEffectiveQuantity,
} from "./worksheetFormValues";
import { worksheetRowGridTemplate } from "./worksheetStyles";
import WorksheetStandardRowFields from "./WorksheetStandardRowFields";
import WorksheetTruckingRowFields from "./WorksheetTruckingRowFields";
import WorksheetMaterialCalcFields from "./WorksheetMaterialCalcFields";
import WorksheetLibraryPickerDialog from "./WorksheetLibraryPickerDialog";
import UiSideModal from "components/base/UiSideModal";
import WorksheetIconButton from "./WorksheetIconButton";

type WorksheetCostRowProps = {
  index: number;
  row: WorksheetItemInput["costRows"][number];
  lineQuantity: string;
  lineUnit: WorksheetHeaderUnit;
  isReadOnly?: boolean;
  resolution: HelperResolution;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddSelections?: (selection: WorksheetLibrarySelection) => void;
  dragHandle: React.ReactNode;
};

export default function WorksheetCostRow({
  index,
  row,
  lineQuantity,
  lineUnit,
  isReadOnly,
  resolution,
  onDuplicate,
  onDelete,
  onAddSelections,
  dragHandle,
}: WorksheetCostRowProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const isTrucking = row.category === WorksheetCostCategory.TRUCKING;
  const isMaterial = row.category === WorksheetCostCategory.MATERIAL;
  const isMaterialCalculated = isMaterial && row.calcMethod !== WorksheetMaterialCalcMethod.MANUAL;
  const costTotal = getRowCost(row, lineQuantity, lineUnit, resolution);
  const plannerActive = row.quantitySource === "trucking";
  const sourceDescription = [
    row.sourceName,
    row.sourcePlantName ? `Plant: ${row.sourcePlantName}` : null,
    row.sourceType === "transfer_truck" && row.truckCapacity
      ? `Capacity: ${row.truckCapacity} US tons`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleLibrarySelect = (selection: WorksheetLibrarySelection) => {
    const next = applyLibrarySelectionToRow(row, selection);

    (Object.keys(next) as Array<keyof typeof next>).forEach((field) => {
      setValue(`costRows.${index}.${field}`, next[field] as never, { shouldDirty: true });
    });
  };

  const handleMoveTo = (category: WorksheetCostCategory) => {
    const next = getMovedCostRow(
      row,
      category,
      getRowEffectiveQuantity(row, lineQuantity, lineUnit, resolution)
    );

    (Object.keys(next) as Array<keyof typeof next>).forEach((field) => {
      setValue(`costRows.${index}.${field}`, next[field] as never, { shouldDirty: true });
    });
    setMenuAnchor(null);
  };

  const handleLibrarySelectMany = (selections: WorksheetLibrarySelection[]) => {
    selections.forEach((selection) => {
      onAddSelections?.(selection);
    });
  };

  return (
    <MDBox
      sx={{
        border: `1px solid ${draftColors.border}`,
        borderRadius: "8px",
        p: "4px 6px",
        backgroundColor: draftColors.surface,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <MDBox
        display="grid"
        gridTemplateColumns={worksheetRowGridTemplate}
        gap="4px"
        alignItems="center"
      >
        <MDBox sx={{ display: "flex", justifyContent: "center", color: draftColors.textSecondary }}>
          {dragHandle}
        </MDBox>

        <MDBox display="flex" alignItems="center" gap="4px">
          <MDBox display="flex" alignItems="center" flex={1} minWidth={0}>
            <Controller
              control={control}
              name={`costRows.${index}.name`}
              render={({ field }) => (
                <UiInput
                  value={field.value}
                  fullWidth
                  readOnly={isReadOnly}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              )}
            />
          </MDBox>
          {!isReadOnly && (
            <Tooltip title={row.sourceName ? `Library: ${sourceDescription}` : "Link library item"}>
              <Icon
                fontSize="small"
                aria-label="Open resource library"
                sx={{ cursor: "pointer", color: draftColors.textAction }}
                onClick={() => setLibraryPickerOpen(true)}
              >
                library_add
              </Icon>
            </Tooltip>
          )}
        </MDBox>

        {isTrucking ? (
          <WorksheetTruckingRowFields
            index={index}
            isReadOnly={isReadOnly}
            plannerActive={plannerActive}
            computedTrucks={resolution.rowTrucks[row.rowUuid]}
            computedQuantity={resolution.rowQuantities[row.rowUuid]?.toNumber()}
          />
        ) : (
          <WorksheetStandardRowFields
            index={index}
            category={row.category}
            isReadOnly={isReadOnly}
            isMaterialCalculated={isMaterialCalculated}
            quantityOverridden={row.quantityOverridden}
            displayedQuantity={String(
              getRowEffectiveQuantity(row, lineQuantity, lineUnit, resolution)
            )}
            trailingAction={
              isMaterial && !isReadOnly ? (
                <WorksheetIconButton
                  icon="calculate"
                  title="Open material calculation"
                  ariaLabel="Open material calculation"
                  onClick={() => setCalcOpen(true)}
                />
              ) : undefined
            }
          />
        )}

        <Controller
          control={control}
          name={`costRows.${index}.rate`}
          render={({ field }) => (
            <UiInput
              value={field.value}
              textAlign="right"
              readOnly={isReadOnly}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />

        <MDBox display="flex" alignItems="center" justifyContent="flex-end" gap="10px" minWidth={0}>
          <UiTypography
            uiVariant="body-sm"
            sx={{ flex: 1, minWidth: 0, textAlign: "right", fontWeight: 600 }}
          >
            {formatCentsToUSD(costTotal)}
          </UiTypography>
          {!isReadOnly && (
            <Tooltip title="Cost row actions">
              <Icon
                fontSize="small"
                aria-label="Open cost row actions"
                sx={{ cursor: "pointer", color: draftColors.textAction, flexShrink: 0 }}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                more_vert
              </Icon>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>

      {isMaterial && (
        <UiSideModal
          open={calcOpen}
          onClose={() => setCalcOpen(false)}
          title="Material Calculation"
          width="498px"
          onConfirm={() => setCalcOpen(false)}
          confirmLabel="Done"
        >
          <MDBox p="16px" display="flex" flexDirection="column" gap="12px">
            <WorksheetMaterialCalcFields
              index={index}
              calcMethod={row.calcMethod}
              isReadOnly={isReadOnly}
              quantityOverridden={row.quantityOverridden}
            />
          </MDBox>
        </UiSideModal>
      )}

      <WorksheetLibraryPickerDialog
        open={libraryPickerOpen}
        category={row.category}
        onClose={() => setLibraryPickerOpen(false)}
        onSelect={handleLibrarySelect}
        onSelectMany={handleLibrarySelectMany}
      />
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onDuplicate();
          }}
        >
          Duplicate cost
        </MenuItem>
        {Object.entries(WORKSHEET_CATEGORY_LABELS)
          .filter(([category]) => category !== row.category)
          .map(([category, label]) => (
            <MenuItem
              key={category}
              onClick={() => handleMoveTo(category as WorksheetCostCategory)}
            >
              Move to {label.toLowerCase()}
            </MenuItem>
          ))}
        <Divider />
        <MenuItem
          sx={{ color: draftColors.red }}
          onClick={() => {
            setMenuAnchor(null);
            onDelete();
          }}
        >
          Delete cost
        </MenuItem>
      </Menu>
    </MDBox>
  );
}
